<script setup lang="ts">
import { logger } from '@/utils/logger';
import { useGaiaToast } from '@/composables/useGaiaToast';
import { computed, ref, watch } from 'vue';
import { getSigningStatistics, type SigningStatistics } from '@/api';
import StatisticsBarChartCard from '@/components/admin/StatisticsBarChartCard.vue';
import StatisticsKpiGrid, {
  type StatisticsKpiTile,
} from '@/components/admin/StatisticsKpiGrid.vue';
import {
  buildStatisticsDateQuery,
  formatStatisticsKeyLabel,
  formatStatisticsNumber,
  sortStatisticsEntries,
  type StatisticsFiltersProps,
} from '@/utils/adminStatistics';
import { formatDateTimeForTable } from '@/utils/datetimeUtils';

type StatisticsSection = {
  title: string;
  keyLabel: string;
  valueLabel: string;
  rows: [string, number][];
  horizontal?: boolean;
};

const props = defineProps<StatisticsFiltersProps>();
const message = useGaiaToast();

const data = ref<SigningStatistics | undefined>(undefined);
const loading = ref(false);

const kpiTiles = computed<StatisticsKpiTile[]>(() => {
  if (data.value === undefined) {
    return [];
  }

  return [
    {
      label: 'Total signing requests',
      value: formatStatisticsNumber(data.value.total_signing_requests),
    },
    { label: 'Total documents', value: formatStatisticsNumber(data.value.total_documents) },
    { label: 'Total participants', value: formatStatisticsNumber(data.value.total_participants) },
    {
      label: 'Avg documents / request',
      value: formatStatisticsNumber(data.value.average_documents_per_request),
    },
    {
      label: 'Avg participants / request',
      value: formatStatisticsNumber(data.value.average_participants_per_request),
    },
    {
      label: 'Avg signers / request',
      value: formatStatisticsNumber(data.value.average_signers_per_request),
    },
  ];
});

const summarySections = computed<StatisticsSection[]>(() => [
  {
    title: 'Requests by provider',
    keyLabel: 'Provider',
    valueLabel: 'Requests',
    rows: sortStatisticsEntries(data.value?.requests_by_provider, 'value-desc'),
    horizontal: true,
  },
  {
    title: 'Requests by status',
    keyLabel: 'Status',
    valueLabel: 'Requests',
    rows: sortStatisticsEntries(data.value?.requests_by_status, 'value-desc'),
    horizontal: true,
  },
  {
    title: 'Participants by role',
    keyLabel: 'Role',
    valueLabel: 'Participants',
    rows: sortStatisticsEntries(data.value?.participants_by_role, 'value-desc'),
    horizontal: true,
  },
  {
    title: 'Participants by status',
    keyLabel: 'Status',
    valueLabel: 'Participants',
    rows: sortStatisticsEntries(data.value?.participants_by_status, 'value-desc'),
    horizontal: true,
  },
  {
    title: 'Documents by type',
    keyLabel: 'Type',
    valueLabel: 'Documents',
    rows: sortStatisticsEntries(data.value?.documents_by_type, 'value-desc'),
    horizontal: true,
  },
  {
    title: 'Participants per request',
    keyLabel: 'Participants',
    valueLabel: 'Requests',
    rows: sortStatisticsEntries(data.value?.participants_per_request, 'key-asc'),
  },
  {
    title: 'Signers per request',
    keyLabel: 'Signers',
    valueLabel: 'Requests',
    rows: sortStatisticsEntries(data.value?.signers_per_request, 'key-asc'),
  },
  {
    title: 'Documents per request',
    keyLabel: 'Documents',
    valueLabel: 'Requests',
    rows: sortStatisticsEntries(data.value?.documents_per_request, 'key-asc'),
  },
]);

const latestSigningRequests = computed(() => data.value?.latest_signing_requests ?? []);

async function fetchStatistics(): Promise<void> {
  loading.value = true;
  try {
    const response = await getSigningStatistics({
      query: {
        included_org_uids: props.includedOrgUids,
        excluded_org_uids: props.excludedOrgUids,
        latest_limit: 10,
        ...buildStatisticsDateQuery(props.dateRange),
      },
    });

    if (response.response?.status === 200) {
      data.value = response.data;
    }
  } catch (error) {
    message.error('Failed to load signing statistics');
    logger.error('Failed to load signing statistics:', error);
  } finally {
    loading.value = false;
  }
}

watch(props, fetchStatistics, { deep: true, immediate: true });
</script>

<template>
  <n-spin :show="loading">
    <n-flex v-if="data !== undefined" vertical :size="16">
      <statistics-kpi-grid :tiles="kpiTiles" />

      <n-grid cols="1 s:1 m:2" responsive="screen" :x-gap="16" :y-gap="16">
        <n-gi v-for="section in summarySections" :key="section.title">
          <statistics-bar-chart-card
            :title="section.title"
            :rows="section.rows"
            :x-label="section.horizontal ? section.valueLabel : section.keyLabel"
            :y-label="section.horizontal ? section.keyLabel : section.valueLabel"
            :horizontal="section.horizontal"
            :format-category="formatStatisticsKeyLabel"
          />
        </n-gi>
      </n-grid>

      <n-card title="Latest signing requests" size="small">
        <n-table size="small" :single-line="false">
          <thead>
            <tr>
              <th>Name</th>
              <th>Provider</th>
              <th>Status</th>
              <th>Organisation</th>
              <th>Participants</th>
              <th>Signers</th>
              <th>Approvers</th>
              <th>Followers</th>
              <th>Documents</th>
              <th>Created</th>
            </tr>
          </thead>
          <tbody v-if="latestSigningRequests.length > 0">
            <tr v-for="request in latestSigningRequests" :key="request.uid">
              <td>{{ request.name || request.uid }}</td>
              <td>{{ formatStatisticsKeyLabel(request.provider) }}</td>
              <td>{{ request.status ? formatStatisticsKeyLabel(request.status) : 'Unknown' }}</td>
              <td>{{ request.organisation_name }}</td>
              <td>{{ request.participant_count }}</td>
              <td>{{ request.signer_count }}</td>
              <td>{{ request.approver_count }}</td>
              <td>{{ request.follower_count }}</td>
              <td>{{ request.document_count }}</td>
              <td>
                <n-text depth="3">{{ formatDateTimeForTable(request.created_at) }}</n-text>
              </td>
            </tr>
          </tbody>
          <tbody v-else>
            <tr>
              <td colspan="10">
                <n-empty description="No signing requests found" />
              </td>
            </tr>
          </tbody>
        </n-table>
      </n-card>
    </n-flex>
    <div v-else style="min-height: 200px" />
  </n-spin>
</template>

<style scoped></style>
